/**
 * Iter 20.08 — Demo-Cases (Iter 20.11: reale KV-Regeln + HR-Sprache).
 *
 * Zwei pre-baked Fälle für die public `/demo`-Route. Kein Backend, keine
 * externen Anfragen, keine gespeicherten Daten. Der demo-Interceptor gibt
 * beim Aufruf der relevanten `/api/tool/*`-Endpoints hart-codierte
 * Responses zurück.
 *
 *   • sarah  — einfacher Standard-Fall (KV Handel Angestellte, 2 Vordienstzeiten)
 *   • julia  — komplexer Case (KV Metallgewerbe, Praktikum, Höchstanrechnung, manuelle Anpassung)
 *
 * Die verwendeten KV-Regeln entsprechen den öffentlich einsehbaren
 * Fassungen 2024/2025 (KV Handel — Angestellte: 7 Jahre Höchstdauer;
 * KV Metallgewerbe — Angestellte: 12 Verwendungsgruppenjahre laut § 15
 * Abs. 3). Personen und Zeiträume sind fiktiv.
 */

const SARAH_KV_ID = "demo-kv-handel-angestellte";
const JULIA_KV_ID = "demo-kv-metallgewerbe";

// KV-Liste, die der Demo-Interceptor für `GET /tool/kvs` zurückgibt. Enthält
// beide Demo-KVs plus ein paar ausgegraute „nicht in dieser Demo verfügbar"-
// KVs, damit die Auswahlliste realistisch wirkt.
export const DEMO_KV_LIST = [
  {
    id: SARAH_KV_ID,
    name: "KV Handel — Angestellte",
    kuerzel: "HANDEL A.",
    is_favorite: false,
    is_beta_for_me: false,
  },
  {
    id: JULIA_KV_ID,
    name: "KV Metallgewerbe",
    kuerzel: "METALL",
    is_favorite: false,
    is_beta_for_me: false,
  },
  {
    id: "demo-kv-gewerbe",
    name: "KV Gewerbe & Handwerk",
    kuerzel: "GEWERBE",
    is_favorite: false,
    is_beta_for_me: false,
  },
  {
    id: "demo-kv-it",
    name: "KV Information & Consulting",
    kuerzel: "IT",
    is_favorite: false,
    is_beta_for_me: false,
  },
];

// ─────────────────────────────────────────────────────────────────────────
// SARAH — Einfacher Standard-Fall
// ─────────────────────────────────────────────────────────────────────────
const SARAH = {
  id: "sarah",
  title: "Sarah, Verkäuferin",
  subtitle: "Standard-Fall · Handel Angestellte",
  minutes: "≈ 90 Sekunden",
  description:
    "2 Vordienstzeiten, klare Tätigkeit, keine Sonderfälle. So sehen 90 % der Klassifikationen aus.",

  // Initial state für EinstufungToolPage (bei Mount injiziert)
  initial: {
    kvId: SARAH_KV_ID,
    stichtag: null, // → wird auf heute gesetzt
    consent: true,
    description:
      "Verkäuferin im Einzelhandel — Kundenberatung, Warenpräsentation, Kassenabrechnung, Warenverräumung. Kein Personalverantwortung.",
  },

  // Response für POST /tool/analyze
  analyzeResponse: {
    ok: true,
    kv: { id: SARAH_KV_ID, name: "KV Handel — Angestellte" },
    files: [],
    ai: {
      confidence_score: 0.92,
      suggested_beschaeftigungsgruppe: "B",
      rationale:
        "Klassische Verkaufstätigkeit im Einzelhandel ohne Führungsverantwortung. " +
        "Die Beschreibung umfasst die typischen Merkmale der Beschäftigungsgruppe B: " +
        "Kundenberatung, Warenpräsentation und Kassenabrechnung.",
      clarifying_questions: [],
      extracted_facts: {
        taetigkeit_kurz: "Verkäuferin im Einzelhandel",
        qualifikation: "keine Angabe",
        berufsjahre: "3",
      },
      model: "demo-mock",
    },
    auto_matched_ausbildung: [],
  },

  // Response für POST /tool/extract-vordienstzeiten
  extractResponse: {
    ok: true,
    kv: { id: SARAH_KV_ID, name: "KV Handel — Angestellte" },
    files: [{ name: "Beispiel-SV-Auszug.pdf", size: 128000 }],
    result: {
      items: [
        {
          datum_von: "2022-03-01",
          datum_bis: "2024-08-31",
          arbeitgeber: "Fashion Store GmbH",
          taetigkeit: "Verkäuferin Einzelhandel",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          _kv_rule_applied: true,
          anrechenbarkeit_begruendung:
            "Einschlägige Angestelltentätigkeit im Einzelhandel — anrechenbar zu 100 % laut KV Handel/Angestellte § 4 Ziff. 3.",
        },
        {
          datum_von: "2020-09-01",
          datum_bis: "2022-02-28",
          arbeitgeber: "Mode Bertl KG",
          taetigkeit: "Verkäuferin Einzelhandel",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          _kv_rule_applied: true,
          anrechenbarkeit_begruendung:
            "Einschlägige Angestelltentätigkeit im Einzelhandel — anrechenbar zu 100 % laut KV Handel/Angestellte § 4 Ziff. 3.",
        },
      ],
      zusammenfassung:
        "2 Vordienstzeiten erkannt, beide vollständig anrechenbar. Gesamtanrechnung: 3 Jahre 11 Monate. Höchstanrechnungsdauer laut KV Handel/Angestellte: 7 Jahre — nicht erreicht.",
      hinweise: [],
      anrechenbare_monate: 47.2,
      stichtag: null,
      model: "demo-mock",
    },
  },

  // Response für POST /tool/final-recommendation
  finalRecommendation: {
    ok: true,
    kv: { id: SARAH_KV_ID, name: "KV Handel — Angestellte" },
    recommendation: {
      beschaeftigungsgruppe: "B",
      gehaltsstufe: "2",
      gruppe_beschreibung:
        "Beschäftigungsgruppe B: Angestellte mit einfachen Tätigkeiten im Verkauf, " +
        "Kundenberatung und Warenpräsentation ohne Personalverantwortung.",
      stufen_beschreibung: "",
      has_stufen_tabelle: true,
      mindestgehalt_brutto: 2124.0,
      anrechenbare_dienstjahre: "3 J. 11 M.",
      anrechenbare_monate: 47,
      anrechenbare_dauer: {
        jahre: 3,
        monate: 11,
        tage: 0,
        display: "3 J. 11 M.",
      },
      stichtag: null,
      begruendung:
        "Sarah wird gemäß KV Handel — Angestellte in Beschäftigungsgruppe B, " +
        "Gehaltsstufe 2 eingestuft. Die Beschäftigungsgruppe folgt aus der " +
        "tatsächlichen Tätigkeit (Verkäuferin im Einzelhandel ohne " +
        "Personalverantwortung). Die anrechenbaren Vordienstzeiten (3 Jahre " +
        "11 Monate als Angestellte im Einzelhandel) unterschreiten die " +
        "Höchstanrechnungsdauer von 7 Jahren deutlich — die Grenze wirkt " +
        "sich hier nicht aus.",
      handlungsempfehlung: "",
      hinweise: [
        "Die nächste Vorrückung erfolgt in 1 Jahr 1 Monat.",
      ],
      next_vorrueckung: null,
      vorrueckung_schedule: [],
      items_updated: [],
      model: "demo-mock",
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────
// JULIA — Komplexer Case mit Sonderfällen
// ─────────────────────────────────────────────────────────────────────────
const JULIA = {
  id: "julia",
  title: "Julia, Functional Safety Managerin",
  subtitle: "Komplex · Metallgewerbe mit Praktikum, Höchstanrechnung & manueller Anpassung",
  minutes: "≈ 3 Minuten",
  description:
    "6 Vordienstzeiten mit Praktikum, selbständiger Zwischenphase und Erreichen der KV-Höchstanrechnung von 12 Verwendungsgruppenjahren. Zeigt, wie ET auch knifflige Fälle sauber löst.",

  initial: {
    kvId: JULIA_KV_ID,
    stichtag: null,
    consent: true,
    description:
      "Functional Safety Manager Automotive — leitet ein interdisziplinäres Team " +
      "in ISO-26262-Compliance, verantwortet Safety-Freigaben für Steuergeräte " +
      "und koordiniert Reviews mit externen Zertifizierungsstellen.",
  },

  analyzeResponse: {
    ok: true,
    kv: { id: JULIA_KV_ID, name: "KV Metallgewerbe" },
    files: [],
    ai: {
      confidence_score: 0.87,
      suggested_beschaeftigungsgruppe: "E",
      rationale:
        "Leitende Position mit fachlicher Verantwortung für Safety-Freigaben, " +
        "Team-Leitung und Koordination externer Stellen. Deutlich über " +
        "Beschäftigungsgruppe D (Facharbeit ohne Führung), aber unter F " +
        "(strategisch-verantwortliche Position auf Bereichsleitungs-Ebene). " +
        "Beschäftigungsgruppe E ist die passende Einstufung.",
      clarifying_questions: [],
      extracted_facts: {
        taetigkeit_kurz: "Functional Safety Manager Automotive",
        qualifikation: "FH-Master Automotive Engineering",
        berufsjahre: "13",
      },
      model: "demo-mock",
    },
    auto_matched_ausbildung: [],
  },

  extractResponse: {
    ok: true,
    kv: { id: JULIA_KV_ID, name: "KV Metallgewerbe" },
    files: [{ name: "Beispiel-CV-Julia.pdf", size: 245000 }],
    result: {
      items: [
        // 1. Praktikum — Anstellungstyp fehlt, wird zum Prüf-Marker (gelb)
        {
          datum_von: "2013-07-01",
          datum_bis: "2013-12-31",
          arbeitgeber: "AVL List GmbH",
          taetigkeit: "Praktikum Testfeld Motorenentwicklung",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          anrechenbarkeit_begruendung:
            "Praktikum erkannt — Anstellungs-Typ (Angestellter/Arbeiter) in Schritt 3 wählen.",
        },
        // 2. Junior Systemingenieurin — einschlägig, KV-Regel (grünes Häkchen)
        {
          datum_von: "2014-02-01",
          datum_bis: "2016-12-31",
          arbeitgeber: "Magna Steyr AG",
          taetigkeit: "Junior Systemingenieurin Fahrwerk",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          _kv_rule_applied: true,
          anrechenbarkeit_begruendung:
            "Einschlägige technische Angestelltentätigkeit in einem Metallgewerbe-Betrieb — anrechenbar zu 100 % gemäß KV Metallgewerbe § 15 Abs. 3.",
        },
        // 3. Sachbearbeiterin — Übergangs-Beschäftigung ohne eindeutige
        //    Einschlägigkeit → das Tool nimmt Faktor 1,0 an und markiert
        //    als „Annahme" zur Prüfung durch HR.
        {
          datum_von: "2017-01-01",
          datum_bis: "2017-08-31",
          arbeitgeber: "Zwischenstation Consulting",
          taetigkeit: "Sachbearbeiterin Projektmanagement",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          anrechenbarkeit_begruendung:
            "Kurze Übergangs-Beschäftigung außerhalb Metallgewerbe — Einschlägigkeit unklar, Prüfung durch HR empfohlen.",
        },
        // 4. Teamleiterin QS — einschlägig, KV-Regel
        {
          datum_von: "2017-09-01",
          datum_bis: "2021-06-30",
          arbeitgeber: "Robert Bosch AG",
          taetigkeit: "Teamleiterin Qualitätssicherung",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          _kv_rule_applied: true,
          anrechenbarkeit_begruendung:
            "Einschlägige Führungstätigkeit in der Qualitätssicherung eines Metallgewerbe-Betriebs — anrechenbar zu 100 % gemäß KV Metallgewerbe § 15 Abs. 3.",
        },
        // 5. Functional Safety Engineer — laufend, einschlägig
        {
          datum_von: "2021-07-01",
          datum_bis: null,
          arbeitgeber: "Continental AG Wien",
          taetigkeit: "Functional Safety Engineer",
          anrechenbar: true,
          anrechnungsfaktor: 1.0,
          _kv_rule_applied: true,
          anrechenbarkeit_begruendung:
            "Einschlägige technische Angestelltentätigkeit — anrechenbar zu 100 %. Laufende Vorbeschäftigung wird bis 1 Tag vor Stichtag berechnet.",
          was_laufend: true,
        },
        // 6. Selbständige Tätigkeit — laut KV nicht anrechenbar
        {
          datum_von: "2011-01-01",
          datum_bis: "2013-06-30",
          arbeitgeber: "Selbständig als Freelance Consultant",
          taetigkeit: "Selbständige Consulting-Tätigkeit",
          anrechenbar: false,
          anrechnungsfaktor: 0.0,
          _kv_rule_applied: true,
          anrechenbarkeit_begruendung:
            "Selbständige Tätigkeit — laut KV Metallgewerbe § 15 Abs. 3 nicht anrechenbar (nur nichtselbständige Beschäftigungsverhältnisse).",
        },
      ],
      zusammenfassung:
        "6 Vordienstzeiten erkannt: 3 vollständig anrechenbar, 1 als Praktikum mit ausstehender Anstellungstyp-Wahl, 1 zur Prüfung (Einschlägigkeit unklar), 1 nicht anrechenbar (selbständig). " +
        "Rechnerische Summe der anrechenbaren Zeit übersteigt die Höchstanrechnungsdauer laut KV Metallgewerbe § 15 Abs. 3 (12 Verwendungsgruppenjahre).",
      hinweise: [
        "Beim Praktikum 2013 bitte Anstellungstyp (Angestellter/Arbeiter) wählen.",
        "Die Höchstanrechnungsdauer von 12 Verwendungsgruppenjahren wird durch die einschlägigen Zeiten voll ausgeschöpft.",
      ],
      anrechenbare_monate: 144,
      stichtag: null,
      model: "demo-mock",
    },
  },

  finalRecommendation: {
    ok: true,
    kv: { id: JULIA_KV_ID, name: "KV Metallgewerbe" },
    recommendation: {
      beschaeftigungsgruppe: "E",
      gehaltsstufe: "6",
      gruppe_beschreibung:
        "Beschäftigungsgruppe E: Angestellte mit selbständiger Fachtätigkeit " +
        "und Führungsverantwortung für ein Team oder einen Fachbereich.",
      stufen_beschreibung: "",
      has_stufen_tabelle: true,
      mindestgehalt_brutto: 4712.0,
      anrechenbare_dienstjahre: "12 J. 0 M.",
      anrechenbare_monate: 144,
      anrechenbare_dauer: {
        jahre: 12,
        monate: 0,
        tage: 0,
        display: "12 J. 0 M.",
      },
      stichtag: null,
      begruendung:
        "Julia wird gemäß KV Metallgewerbe in Beschäftigungsgruppe E, Gehaltsstufe 6 " +
        "eingestuft. Die Beschäftigungsgruppe folgt aus der tatsächlichen Tätigkeit " +
        "(fachliche Führungsverantwortung mit externem Stakeholder-Kontakt). " +
        "Die rechnerisch anrechenbaren Vordienstzeiten übersteigen die " +
        "Höchstanrechnungsdauer von 12 Verwendungsgruppenjahren nach KV Metallgewerbe " +
        "§ 15 Abs. 3 — diese Grenze wird daher voll ausgeschöpft.",
      handlungsempfehlung:
        "Die Höchstanrechnungsdauer ist erreicht — zusätzliche Vordienstzeiten würden das Ergebnis nicht mehr verändern.",
      hinweise: [
        "Höchstanrechnungsdauer laut KV Metallgewerbe § 15 Abs. 3 (12 Verwendungsgruppenjahre) ist erreicht.",
        "Selbständige Tätigkeit wurde korrekt nicht angerechnet.",
        "Die Übergangs-Beschäftigung 2017 ist als »Annahme« markiert — bitte auf Einschlägigkeit prüfen.",
      ],
      next_vorrueckung: null,
      vorrueckung_schedule: [],
      items_updated: [],
      model: "demo-mock",
    },
  },
};

export const DEMO_CASES = { sarah: SARAH, julia: JULIA };

export function getDemoCase(caseId) {
  return DEMO_CASES[caseId] || null;
}
